<script lang="ts">
import { countries } from "$lib/seo/countries";
import {
  CHECK_DIGIT_GAP,
  CHECK_DIGIT_STEPS,
  CHECKER_FAQ,
  COMPOSITE_DIGIT,
  EXPLAINER_LINKS,
  fieldPositions,
  MRZ_DEFINITION,
  WORKED_EXAMPLE,
  WORKED_EXAMPLE_LEAD,
} from "$lib/seo/mrz-explainer";
import { type MrzParseResult, parseMrz } from "$lib/seo/mrz-parser";
import SeoHead from "$lib/seo/SeoHead.svelte";
import SiteFooter from "$lib/seo/SiteFooter.svelte";
import { specimenFormatZones } from "$lib/seo/zones";

const title = "MRZ parser and check digit calculator – TD1, TD2 and TD3";
const description =
  "Paste a passport or ID card MRZ (TD1, TD2 or TD3): every field parsed and every ICAO 9303 check digit verified, in your browser. Nothing is uploaded.";

// The specimen's own three documents — an invented holder, the same one every
// page of this site uses — so each example is a zone that passes.
const examples = specimenFormatZones().map((zone) => ({
  format: zone.format,
  text: zone.lines.join("\n"),
}));

// The issuing state and the nationality are shown with the country's name
// where the marketing table knows the code.
const names: Record<string, string> = Object.fromEntries(
  countries.map((country) => [country.alpha3, country.name]),
);

const positions = fieldPositions();

let input = $state("");

// Parsed on every change rather than on a button: the result follows the text,
// so fixing one character shows at once whether the digit now passes.
const result: MrzParseResult | null = $derived(
  input.trim() === "" ? null : parseMrz(input, { names }),
);

const failing = $derived(result?.checks.filter((c) => !c.valid) ?? []);

function load(text: string): void {
  input = text;
}
</script>

<SeoHead {title} {description} path="/mrz-checker" />

<div>
  <main class="mx-auto max-w-3xl px-6 py-10">
    <h1 class="text-3xl font-semibold tracking-tight sm:text-4xl">
      MRZ parser and check digit calculator
    </h1>
    <p class="mt-4 max-w-2xl text-lg text-slate-600">
      Paste the machine-readable zone of a passport, identity card or visa – TD3, TD2 or TD1. The
      format is detected, every field is read out, and every ICAO 9303 check digit is recomputed and
      compared with the one printed. It all runs in your browser; nothing leaves the page.
    </p>

    <div class="mt-8">
      <label for="mrz-input" class="block text-sm font-medium text-slate-700">
        MRZ (two or three lines)
      </label>
      <textarea
        id="mrz-input"
        bind:value={input}
        rows="3"
        spellcheck="false"
        autocomplete="off"
        autocapitalize="characters"
        placeholder="Paste the lines of the zone here"
        class="mt-2 w-full rounded-lg border border-slate-300 bg-white p-3 font-mono text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
      ></textarea>
      <div class="mt-3 flex flex-wrap gap-3">
        {#each examples as example (example.format)}
          <button
            type="button"
            onclick={() => load(example.text)}
            class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            Load {example.format} example
          </button>
        {/each}
        {#if input !== ""}
          <button
            type="button"
            onclick={() => load("")}
            class="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Clear
          </button>
        {/if}
      </div>
    </div>

    {#if result}
      <section class="mt-8" aria-live="polite">
        {#each result.notes as note (note)}
          <p class="mb-2 text-sm text-slate-500">{note}</p>
        {/each}

        {#if !result.ok}
          <p class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {result.errors.join(" ")}
          </p>
        {:else}
          <p class="text-sm text-slate-600">
            Format: <span class="font-medium text-slate-900">{result.formatName}</span>
          </p>
          <p
            class="mt-3 rounded-md px-4 py-3 text-sm font-medium {result.checksumsValid
              ? 'border border-green-200 bg-green-50 text-green-800'
              : 'border border-amber-200 bg-amber-50 text-amber-800'}"
            role="status"
          >
            {#if result.checksumsValid}
              All {result.checks.length} check digits are valid.
            {:else}
              {failing.length} of {result.checks.length} check digits do not match:
              {failing.map((c) => c.label).join(", ")}. A mismatch almost always means one character
              of that field was misread or mistyped.
            {/if}
          </p>

          {#if result.warnings.length > 0}
            <ul class="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-800">
              {#each result.warnings as warning (warning)}
                <li>{warning}</li>
              {/each}
            </ul>
          {/if}

          <h2 class="mt-6 text-lg font-semibold text-slate-900">Check digits</h2>
          <div class="mt-3 overflow-x-auto">
            <table class="w-full border-collapse text-sm">
              <thead>
                <tr class="text-left text-slate-500">
                  <th scope="col" class="py-2 pr-4 font-medium">Covers</th>
                  <th scope="col" class="py-2 pr-4 font-medium">Where</th>
                  <th scope="col" class="py-2 pr-4 font-medium">Printed</th>
                  <th scope="col" class="py-2 pr-4 font-medium">Computed</th>
                  <th scope="col" class="py-2 font-medium">Result</th>
                </tr>
              </thead>
              <tbody>
                {#each result.checks as check (check.key)}
                  <tr class="border-t border-slate-200">
                    <td class="py-2 pr-4 text-slate-700">{check.label}</td>
                    <td class="py-2 pr-4 whitespace-nowrap text-slate-500">{check.where}</td>
                    <td class="py-2 pr-4 font-mono text-slate-700">{check.printed}</td>
                    <td class="py-2 pr-4 font-mono text-slate-700">{check.computed ?? "–"}</td>
                    <td class="py-2 {check.valid ? 'text-green-700' : 'text-red-700'}">
                      {check.valid ? "valid" : "invalid"}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>

          <h2 class="mt-6 text-lg font-semibold text-slate-900">Parsed fields</h2>
          <div class="mt-3 overflow-x-auto">
            <table class="w-full border-collapse text-sm">
              <thead>
                <tr class="text-left text-slate-500">
                  <th scope="col" class="py-2 pr-4 font-medium">Field</th>
                  <th scope="col" class="py-2 pr-4 font-medium">Value</th>
                  <th scope="col" class="py-2 pr-4 font-medium">As printed</th>
                  <th scope="col" class="py-2 font-medium">Where</th>
                </tr>
              </thead>
              <tbody>
                {#each result.fields as field (field.key)}
                  <tr class="border-t border-slate-200">
                    <td class="py-2 pr-4 text-slate-500">{field.label}</td>
                    <td class="py-2 pr-4 text-slate-900">{field.value}</td>
                    <td class="py-2 pr-4 font-mono text-xs break-all text-slate-600">{field.raw}</td>
                    <td class="py-2 whitespace-nowrap text-slate-500">{field.where}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </section>
    {/if}

    <!-- The explainer below the tool, not above it: somebody who arrived with a
         zone to check wants the box first. Somebody who arrived having typed
         "mrz check digit algorithm" wants this, and reaches it by scrolling
         once. -->
    <section class="mt-16" aria-labelledby="what-is-an-mrz">
      <h2 id="what-is-an-mrz" class="text-2xl font-semibold tracking-tight">
        What a machine-readable zone is
      </h2>
      <p class="mt-4 text-slate-600">{MRZ_DEFINITION}</p>
    </section>

    <section class="mt-12" aria-labelledby="field-positions">
      <h2 id="field-positions" class="text-2xl font-semibold tracking-tight">
        Where each field sits in TD1, TD2 and TD3
      </h2>
      <p class="mt-4 text-slate-600">
        Positions are counted from 1 on each line, the way ICAO 9303 numbers them. Every check digit
        sits right after the field it protects; the composite digit is the last character of its
        line.
      </p>
      <div class="mt-4 overflow-x-auto">
        <table class="w-full border-collapse text-sm">
          <caption class="sr-only">Field and check-digit positions in the three MRZ layouts</caption>
          <thead>
            <tr class="text-left text-slate-500">
              <th scope="col" class="py-2 pr-4 font-medium">Field</th>
              <th scope="col" class="py-2 pr-4 font-medium">TD3 (passport)</th>
              <th scope="col" class="py-2 pr-4 font-medium">TD2</th>
              <th scope="col" class="py-2 font-medium">TD1 (ID card)</th>
            </tr>
          </thead>
          <tbody>
            {#each positions as row, index (index)}
              <tr class="border-t border-slate-200">
                <th scope="row" class="py-2 pr-4 text-left font-normal text-slate-700">{row.label}</th>
                <td class="py-2 pr-4 whitespace-nowrap text-slate-600">{row.td3}</td>
                <td class="py-2 pr-4 whitespace-nowrap text-slate-600">{row.td2}</td>
                <td class="py-2 whitespace-nowrap text-slate-600">{row.td1}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </section>

    <section class="mt-12" aria-labelledby="check-digit">
      <h2 id="check-digit" class="text-2xl font-semibold tracking-tight">
        How a check digit is calculated
      </h2>
      <ol class="mt-4 list-decimal space-y-2 pl-6 text-slate-600">
        {#each CHECK_DIGIT_STEPS as step (step)}
          <li>{step}</li>
        {/each}
      </ol>

      <p class="mt-6 text-slate-600">{WORKED_EXAMPLE_LEAD}</p>
      <div class="mt-4 overflow-x-auto">
        <table class="w-full border-collapse text-sm">
          <caption class="sr-only">
            The 7-3-1 weighting worked through, character by character, on a specimen document
            number
          </caption>
          <thead>
            <tr class="text-left text-slate-500">
              <th scope="col" class="py-2 pr-4 font-medium">Character</th>
              <th scope="col" class="py-2 pr-4 font-medium">Value</th>
              <th scope="col" class="py-2 pr-4 font-medium">Weight</th>
              <th scope="col" class="py-2 font-medium">Product</th>
            </tr>
          </thead>
          <tbody>
            {#each WORKED_EXAMPLE.steps as step, index (index)}
              <tr class="border-t border-slate-200">
                <td class="py-2 pr-4 font-mono text-slate-900">{step.char}</td>
                <td class="py-2 pr-4 text-slate-700">{step.value}</td>
                <td class="py-2 pr-4 text-slate-700">{step.weight}</td>
                <td class="py-2 text-slate-700">{step.product}</td>
              </tr>
            {/each}
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-slate-300">
              <th scope="row" colspan="3" class="py-2 pr-4 text-left font-medium text-slate-700">
                Sum, then modulo 10
              </th>
              <td class="py-2 font-medium text-slate-900">
                {WORKED_EXAMPLE.sum} → {WORKED_EXAMPLE.digit}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p class="mt-4 text-slate-600">
        So the digit printed after <code class="font-mono text-sm">{WORKED_EXAMPLE.field}</code> in that
        zone is
        <code class="font-mono text-sm font-semibold text-slate-900">{WORKED_EXAMPLE.digit}</code>.
        Load the TD3 example above and the parser recomputes the same number in front of you.
      </p>

      <h3 class="mt-8 text-lg font-semibold text-slate-900">The composite digit</h3>
      <p class="mt-2 text-slate-600">{COMPOSITE_DIGIT}</p>

      <h3 class="mt-8 text-lg font-semibold text-slate-900">What the digits do not cover</h3>
      <p class="mt-2 text-slate-600">{CHECK_DIGIT_GAP}</p>
    </section>

    <section class="mt-12" aria-labelledby="faq">
      <h2 id="faq" class="text-2xl font-semibold tracking-tight">Questions people ask</h2>
      <div class="mt-5 divide-y divide-slate-200 border-y border-slate-200">
        {#each CHECKER_FAQ as entry (entry.id)}
          <details class="group py-4">
            <summary
              class="cursor-pointer list-none text-base font-medium text-slate-900 marker:content-none"
            >
              <h3 class="inline">{entry.question}</h3>
              <span aria-hidden="true" class="ml-2 text-slate-400 group-open:hidden">+</span>
              <span aria-hidden="true" class="ml-2 hidden text-slate-400 group-open:inline">−</span>
            </summary>
            <p class="mt-3 text-slate-600">{entry.answer}</p>
          </details>
        {/each}
      </div>
    </section>

    <section class="mt-12" aria-labelledby="next">
      <h2 id="next" class="text-2xl font-semibold tracking-tight">Where to go next</h2>
      <ul class="mt-5 space-y-3 text-slate-600">
        <li>
          <a href="/mrz-generator" class="font-medium text-slate-900 underline hover:text-slate-600"
            >MRZ generator</a
          > – build a valid zone with correct check digits for a test fixture.
        </li>
        {#each EXPLAINER_LINKS as link (link.href)}
          <li>
            <a href={link.href} class="font-medium text-slate-900 underline hover:text-slate-600"
              >{link.label}</a
            > – {link.blurb}
          </li>
        {/each}
      </ul>
    </section>
  </main>

  <SiteFooter />
</div>
